// ShowingBooker API Service
// Connects React frontend to backend API

import { auth } from '../firebase';

// Temporary fix: Force correct API URL for production
const getAPIBaseURL = () => {
  if (window.location.hostname === 'showingbooker.com') {
    return 'https://api.dreamhotties.com';
  }
  return process.env.REACT_APP_API_URL || 'http://localhost:5001';
};

const API_BASE_URL = getAPIBaseURL();
console.log('🌐 ShowingBooker API Base URL:', API_BASE_URL);

// Helper function to get fresh Firebase auth token
const getAuthToken = async () => {
  const user = auth.currentUser;
  if (!user) {
    console.warn('🔐 No authenticated user found');
    throw new Error('No authenticated user found');
  }
  
  try {
    // Check if user is properly authenticated
    if (!user.emailVerified && user.email !== 'david@codexdeus.com') {
      console.warn('🔐 User email not verified');
    }
    
    // Get fresh token from Firebase (will auto-refresh if needed)
    const token = await user.getIdToken(true); // force refresh
    console.log('🔐 Got fresh auth token for user:', user.email);
    return token;
  } catch (error) {
    console.error('🔐 Failed to get auth token:', error);
    // Try to sign out and back in if token is invalid
    if (error.code === 'auth/user-token-expired' || error.code === 'auth/invalid-user-token') {
      console.log('🔐 Token expired, attempting to refresh authentication...');
      try {
        await auth.signOut();
        window.location.reload();
      } catch (signOutError) {
        console.error('Failed to sign out:', signOutError);
      }
    }
    throw new Error('Failed to get authentication token');
  }
};

// Helper function for API requests
const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  // Add auth token if required (and not a public endpoint)
  if (!endpoint.includes('/public/') && !options.noAuth) {
    try {
      const token = await getAuthToken();
      config.headers.Authorization = `Bearer ${token}`;
    } catch (error) {
      console.warn('No auth token available for API request');
    }
  }

  try {
    console.log(`🌐 API Request: ${config.headers.Authorization ? 'Authenticated' : 'Public'} ${endpoint}`);
    const response = await fetch(url, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
      
      // Handle specific 401 authentication errors
      if (response.status === 401) {
        console.error('🔐 Authentication failed for endpoint:', endpoint);
        if (errorData.error === 'Invalid token' || errorData.error === 'No token provided') {
          // Force token refresh and retry
          console.log('🔐 Attempting to refresh authentication...');
          try {
            await auth.currentUser?.getIdToken(true);
          } catch (refreshError) {
            console.error('🔐 Token refresh failed, signing out...', refreshError);
            await auth.signOut();
            window.location.href = '/login';
            return;
          }
        }
      }
      
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('🚨 API request failed:', endpoint, error.message);
    throw error;
  }
};

// API Methods
export const showingbookerAPI = {
  // Test API connection
  testConnection: async () => {
    return apiRequest('/api-test', { noAuth: true });
  },

  // Test database connection
  testDatabase: async () => {
    return apiRequest('/db-test', { noAuth: true });
  },

  // Get agent dashboard stats
  getDashboardStats: async () => {
    return apiRequest('/api/showingbooker/dashboard');
  },

  // Get agent's properties
  getProperties: async () => {
    return apiRequest('/api/showingbooker/properties');
  },

  // Create new property
  createProperty: async (propertyData) => {
    return apiRequest('/api/showingbooker/properties', {
      method: 'POST',
      body: JSON.stringify(propertyData),
    });
  },

  // Update property
  updateProperty: async (propertyId, propertyData) => {
    return apiRequest(`/api/showingbooker/properties/${propertyId}`, {
      method: 'PUT',
      body: JSON.stringify(propertyData),
    });
  },

  // Delete property
  deleteProperty: async (propertyId) => {
    return apiRequest(`/api/showingbooker/properties/${propertyId}`, {
      method: 'DELETE',
    });
  },

  // Get property bookings
  getPropertyBookings: async (propertyId) => {
    return apiRequest(`/api/showingbooker/properties/${propertyId}/bookings`);
  },

  // PUBLIC ENDPOINTS (no auth required)

  // Get public property info for booking
  getPublicProperty: async (propertyId) => {
    return apiRequest(`/api/showingbooker/public/property/${propertyId}`, { noAuth: true });
  },

  // Get public property availability info (for date/time picker)
  getPublicPropertyAvailability: async (propertyId) => {
    return apiRequest(`/api/showingbooker/public/property/${propertyId}/availability`, { noAuth: true });
  },

  // Create booking (public)
  createBooking: async (bookingData) => {
    return apiRequest('/api/showingbooker/public/book', {
      method: 'POST',
      body: JSON.stringify(bookingData),
      noAuth: true,
    });
  },

  // PAYMENT ENDPOINTS

  // Create Stripe payment intent
  createPaymentIntent: async (amount, bookingData) => {
    return apiRequest('/api/payments/create-payment-intent', {
      method: 'POST',
      body: JSON.stringify({ amount, bookingData }),
      noAuth: true,
    });
  },

  // Process booking after payment
  processBooking: async (paymentIntentId, bookingData, propertyData) => {
    return apiRequest('/api/payments/process-booking', {
      method: 'POST',
      body: JSON.stringify({ paymentIntentId, bookingData, propertyData }),
      noAuth: true,
    });
  },

  // Create manual booking entry
  createManualBooking: async (bookingData) => {
    return apiRequest('/api/showingbooker/manual-booking', {
      method: 'POST',
      body: JSON.stringify(bookingData),
    });
  },

  // OWNERS (LANDLORDS) ENDPOINTS
  
  // Get all owners for the agent
  getOwners: async () => {
    return apiRequest('/api/showingbooker/landlords');
  },

  // Create new owner
  createOwner: async (ownerData) => {
    return apiRequest('/api/showingbooker/landlords', {
      method: 'POST',
      body: JSON.stringify(ownerData),
    });
  },

  // Update owner
  updateOwner: async (ownerId, ownerData) => {
    return apiRequest(`/api/showingbooker/landlords/${ownerId}`, {
      method: 'PUT',
      body: JSON.stringify(ownerData),
    });
  },

  // Delete owner
  deleteOwner: async (ownerId) => {
    return apiRequest(`/api/showingbooker/landlords/${ownerId}`, {
      method: 'DELETE',
    });
  },

  // RESIDENTS ENDPOINTS
  
  // Get all residents for the agent
  getResidents: async () => {
    return apiRequest('/api/showingbooker/residents');
  },

  // Create new resident
  createResident: async (residentData) => {
    return apiRequest('/api/showingbooker/residents', {
      method: 'POST',
      body: JSON.stringify(residentData),
    });
  },

  // Update resident
  updateResident: async (residentId, residentData) => {
    return apiRequest(`/api/showingbooker/residents/${residentId}`, {
      method: 'PUT',
      body: JSON.stringify(residentData),
    });
  },

  // Delete resident
  deleteResident: async (residentId) => {
    return apiRequest(`/api/showingbooker/residents/${residentId}`, {
      method: 'DELETE',
    });
  },

  // ADMIN ENDPOINTS
  
  // Create manual booking (admin only)
  createAdminManualBooking: async () => {
    return apiRequest('/api/showingbooker/admin/manual-booking', {
      method: 'POST'
    });
  },

  // Get all agents (admin only) 
  getAllAgents: async () => {
    return apiRequest('/api/showingbooker/admin/agents');
  },

  // Create property for any agent (admin only)
  createPropertyForAgent: async (agentId, propertyData) => {
    return apiRequest('/api/showingbooker/admin/properties', {
      method: 'POST',
      body: JSON.stringify({ agent_id: agentId, ...propertyData }),
    });
  },

  // SHARING ENDPOINTS

  // Get shared property (public)
  getSharedProperty: async (propertySlug) => {
    return apiRequest(`/api/showingbooker/public/share/${propertySlug}`, { noAuth: true });
  },

  // Track property view (public)
  trackPropertyView: async (propertySlug) => {
    return apiRequest(`/api/showingbooker/public/share/${propertySlug}/view`, {
      method: 'POST',
      noAuth: true,
    });
  },

  // Track property share (public)
  trackPropertyShare: async (propertySlug, platform) => {
    return apiRequest(`/api/showingbooker/public/share/${propertySlug}/share`, {
      method: 'POST',
      body: JSON.stringify({ platform }),
      noAuth: true,
    });
  },

  // DEMO DATA ENDPOINTS

  // Create demo booking (authenticated)
  createDemoBooking: async (bookingData) => {
    return apiRequest('/api/showingbooker/demo/booking', {
      method: 'POST',
      body: JSON.stringify(bookingData),
    });
  },

  // AGENTS CONTACTS ENDPOINTS

  // Get all agent contacts for the current user
  getAgentsContacts: async () => {
    return apiRequest('/api/showingbooker/agents-contacts');
  },

  // Create new agent contact
  createAgentContact: async (agentData) => {
    return apiRequest('/api/showingbooker/agents-contacts', {
      method: 'POST',
      body: JSON.stringify(agentData),
    });
  },

  // Update agent contact
  updateAgentContact: async (agentId, agentData) => {
    return apiRequest(`/api/showingbooker/agents-contacts/${agentId}`, {
      method: 'PUT',
      body: JSON.stringify(agentData),
    });
  },

  // Delete agent contact
  deleteAgentContact: async (agentId) => {
    return apiRequest(`/api/showingbooker/agents-contacts/${agentId}`, {
      method: 'DELETE',
    });
  },

  // RENTERS CONTACTS ENDPOINTS

  // Get all renter contacts for the current user
  getRentersContacts: async () => {
    return apiRequest('/api/showingbooker/renters-contacts');
  },

  // Create new renter contact
  createRenterContact: async (renterData) => {
    return apiRequest('/api/showingbooker/renters-contacts', {
      method: 'POST',
      body: JSON.stringify(renterData),
    });
  },

  // Update renter contact
  updateRenterContact: async (renterId, renterData) => {
    return apiRequest(`/api/showingbooker/renters-contacts/${renterId}`, {
      method: 'PUT',
      body: JSON.stringify(renterData),
    });
  },

  // Delete renter contact
  deleteRenterContact: async (renterId) => {
    return apiRequest(`/api/showingbooker/renters-contacts/${renterId}`, {
      method: 'DELETE',
    });
  },

  // BUYERS CONTACTS ENDPOINTS

  // Get all buyer contacts for the current user (from bookings)
  getBuyersContacts: async () => {
    return apiRequest('/api/showingbooker/buyers-contacts');
  },

  // AGENT PROFILE ENDPOINTS

  // Get current agent's profile
  getAgentProfile: async () => {
    return apiRequest('/api/showingbooker/agent/profile');
  },

  // Update current agent's profile
  updateAgentProfile: async (profileData) => {
    return apiRequest('/api/showingbooker/agent/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  },

  // PROSPECTS ENDPOINTS (Unified Agents, Buyers, Renters)

  // Get all prospects for the current agent
  getProspects: async () => {
    return apiRequest('/api/showingbooker/prospects');
  },

  // Create new prospect
  createProspect: async (prospectData) => {
    return apiRequest('/api/showingbooker/prospects', {
      method: 'POST',
      body: JSON.stringify(prospectData),
    });
  },

  // Update prospect
  updateProspect: async (prospectId, prospectData) => {
    return apiRequest(`/api/showingbooker/prospects/${prospectId}`, {
      method: 'PUT',
      body: JSON.stringify(prospectData),
    });
  },

  // Delete prospect
  deleteProspect: async (prospectId) => {
    return apiRequest(`/api/showingbooker/prospects/${prospectId}`, {
      method: 'DELETE',
    });
  },

  // SHOWINGS & CALENDAR ENDPOINTS

  // Get upcoming showings (next 14 days)
  getUpcomingShowings: async () => {
    return apiRequest('/api/showingbooker/upcoming-showings');
  },

  // Get calendar data
  getCalendarData: async (startDate, endDate) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const queryString = params.toString();
    const endpoint = queryString ? `/api/showingbooker/calendar-data?${queryString}` : '/api/showingbooker/calendar-data';
    
    return apiRequest(endpoint);
  },

  // Get completed showings
  getCompletedShowings: async (limit = 50, offset = 0) => {
    return apiRequest(`/api/showingbooker/completed-showings?limit=${limit}&offset=${offset}`);
  },

  // Sync prospects from confirmed bookings
  syncProspects: async () => {
    return apiRequest('/api/showingbooker/sync-prospects', {
      method: 'POST',
    });
  },
};


export default showingbookerAPI;